import { useEffect, useState } from 'react'

const MAIN_KEY = process.env.REACT_APP_LOCAL_STORAGE_KEY || 'applicationState'
const USER_KEY = '_currentUser'

// Helper function to check if storage should be cleared for a new user
function shouldClearStorageForNewUser(currentUserId: string): boolean {
  if (typeof window === 'undefined') return false

  const storedAppState = localStorage.getItem(MAIN_KEY)
  if (!storedAppState) return false

  try {
    const parsedAppState = JSON.parse(storedAppState)
    const storedUserId = parsedAppState[USER_KEY]

    // Clear if there's a stored user and it's different from current user
    return storedUserId && storedUserId !== currentUserId
  } catch {
    return true // Clear on parse error
  }
}

// Helper function to clear all application data
function clearAllApplicationData(): void {
  if (typeof window === 'undefined') return

  // Remove the main key
  localStorage.removeItem(MAIN_KEY)

  // Clean up any legacy keys
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const storageKey = localStorage.key(i)
    if (storageKey && (storageKey.includes('applicationState') || storageKey.includes('purcentApp'))) {
      localStorage.removeItem(storageKey)
    }
  }
}

export default function useLocalStorage<ValueType>(key: string, defaultValue: ValueType, userId?: string | null) {
  const [value, setValue] = useState<ValueType>(() => {
    if (typeof window === 'undefined') {
      return defaultValue
    }

    // Check if we need to clear storage due to user change
    if (userId && shouldClearStorageForNewUser(userId)) {
      clearAllApplicationData()
      return defaultValue
    }

    // Remove legacy keys (existing logic)
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const storageKey = localStorage.key(i)
      if (
        storageKey &&
        storageKey !== MAIN_KEY &&
        (storageKey.includes('applicationState') || storageKey.includes('purcentApp'))
      ) {
        localStorage.removeItem(storageKey)
      }
    }

    const storedAppState = localStorage.getItem(MAIN_KEY)
    if (!storedAppState) {
      return defaultValue
    }

    try {
      const parsedAppState = JSON.parse(storedAppState)
      return parsedAppState && parsedAppState.hasOwnProperty(key) ? parsedAppState[key] : defaultValue
    } catch (error) {
      console.error('Error parsing app state:', error)
      return defaultValue
    }
  })

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (typeof window !== 'undefined' && e.storageArea === localStorage && e.key === MAIN_KEY) {
        try {
          const parsedAppState = e.newValue ? JSON.parse(e.newValue) : {}
          setValue(parsedAppState && parsedAppState.hasOwnProperty(key) ? parsedAppState[key] : defaultValue)
        } catch (error) {
          console.error('Error parsing app state:', error)
          setValue(defaultValue)
        }
      }
    }

    window.addEventListener('storage', listener)
    return () => {
      window.removeEventListener('storage', listener)
    }
  }, [key, defaultValue])

  const setValueInLocalStorage = (newValue: ValueType | ((currentValue: ValueType) => ValueType)) => {
    setValue((currentValue) => {
      const result = typeof newValue === 'function' ? (newValue as Function)(currentValue) : newValue

      if (typeof window !== 'undefined') {
        const storedAppState = localStorage.getItem(MAIN_KEY)
        let parsedAppState: Record<string, any> = {}

        if (storedAppState) {
          try {
            parsedAppState = JSON.parse(storedAppState)
          } catch {
            parsedAppState = {}
          }
        }

        // Store current user ID if provided
        if (userId) {
          parsedAppState[USER_KEY] = userId
        }

        parsedAppState[key] = result
        localStorage.setItem(MAIN_KEY, JSON.stringify(parsedAppState))
      }

      return result
    })
  }

  const getValueInLocalStorage = () => {
    if (typeof window !== 'undefined') {
      const storedAppState = localStorage.getItem(MAIN_KEY)
      if (storedAppState) {
        try {
          const parsedAppState = JSON.parse(storedAppState)
          return parsedAppState[key] ?? defaultValue
        } catch {
          return defaultValue
        }
      }
    }
    return defaultValue
  }

  const clearUserData = () => {
    if (typeof window !== 'undefined') {
      clearAllApplicationData()
      setValue(defaultValue)
    }
  }

  return [value, setValueInLocalStorage, getValueInLocalStorage, clearUserData] as const
}

// Export utility functions for manual clearing
export { clearAllApplicationData, shouldClearStorageForNewUser }
